/**
 * Deterministic per-mission star scoring for Lemonade Leaders Jr.
 *
 * Stars (current scope, kept intentionally simple):
 *   - 1 star: mission completed
 *   - 2 stars: student customized their answer (didn't only tap an example/sample)
 *   - 3 stars: reserved for future use
 *
 * Customization is tracked via `state.customizedMissions: number[]` —
 * a small additive field on the existing localStorage state object.
 */

import { JR_PROBLEM_OPTIONS } from "@/utils/jrDemoState";

const SAMPLE_PITCHES = [
  "My business helps students stay ready to learn by giving them pencils when they need one.",
  "My business helps classmates feel encouraged by sharing kindness cards.",
];
const PITCH_STARTER_TEMPLATE = "My business helps ______ by ______.";

export function computeMissionStars(state, n) {
  const completed = state?.completedMissions || [];
  if (!completed.includes(n)) return 0;

  const explicit = (state?.customizedMissions || []).includes(n);
  if (explicit) return 2;

  // Fallback: infer customization from saved values where we can,
  // so older saves still earn 2 stars when the student wrote their own thing.
  switch (n) {
    case 1:
      // Mission 1 is always free-text — completing it is customization.
      return state?.problem ? 2 : 1;
    case 3: {
      if (state?.problemChoice === "custom" && state?.customProblem) return 2;
      return 1;
    }
    case 4: {
      const c = (state?.customer || "").trim().toLowerCase();
      const presets = [
        "students who lose pencils",
        "classmates who need encouragement",
        "families who need reminders",
        "teachers who need classroom helpers",
      ];
      return c && !presets.includes(c) ? 2 : 1;
    }
    case 8: {
      const p = (state?.pitch || "").trim();
      if (!p) return 1;
      // Tapped one of the canned examples or starter? 1 star. Anything else: 2.
      if (SAMPLE_PITCHES.includes(p)) return 1;
      if (p === PITCH_STARTER_TEMPLATE) return 1;
      return 2;
    }
    default:
      return 1;
  }
}

/**
 * Returns the unlocked badges for the current state.
 */
export function computeBadges(state) {
  const c = new Set(state?.completedMissions || []);
  return {
    firstIdea: c.has(1),
    bravePitch: c.has(8),
    juniorCEO: [1, 2, 3, 4, 5, 6, 7, 8, 9].every((n) => c.has(n)),
  };
}

// Re-export so consumers don't need to import from two places.
export { JR_PROBLEM_OPTIONS };
